// ============================================================================
// CLOSING TRANSACTION HOOKS
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { closingService } from 'api/services/closingService';
import type { ClosingTransaction, CreateClosingPayload, UpdateClosingPayload } from 'api/types/closing.types';
import { notifyDataChanged, useDataChangeListener } from './useDataSync';

const DATA_KEY = 'closings';

export const useClosings = () => {
  const [data, setData] = useState<ClosingTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchClosings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await closingService.getAll({ pageSize: 50000 });
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClosings();
  }, [fetchClosings]);

  useDataChangeListener(DATA_KEY, fetchClosings);

  return { data, loading, error, refetch: fetchClosings };
};

export const useCreateClosing = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createClosing = useCallback(async (payload: CreateClosingPayload) => {
    setLoading(true);
    setError(null);
    try {
      const response = await closingService.create(payload);
      notifyDataChanged(DATA_KEY);
      return response.data;
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { createClosing, loading, error };
};

export const useUpdateClosing = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const updateClosing = useCallback(async (id: string, payload: UpdateClosingPayload) => {
    setLoading(true);
    setError(null);
    try {
      const response = await closingService.update(id, payload);
      notifyDataChanged(DATA_KEY);
      return response.data;
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { updateClosing, loading, error };
};

export const useSubmitClosing = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const submitClosing = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await closingService.submit(id);
      notifyDataChanged(DATA_KEY);
      return response.data;
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { submitClosing, loading, error };
};

export const useDeleteClosing = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const deleteClosing = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      await closingService.delete(id);
      notifyDataChanged(DATA_KEY);
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { deleteClosing, loading, error };
};
